import * as THREE from 'three';
import { COMMON_UNIFORMS, SKY_FNS } from '../shaders/common';
import type { Environment } from './Environment';

const CLOUD_FNS = /* glsl */ `
float cloudDensity(vec3 dir) {
  if (dir.y <= 0.0) return 0.0;
  vec2 p = dir.xz / (dir.y + 0.09);
  vec2 uv = p * 0.3 + uCloudOffset * 0.00022;
  float n = texture2D(uNoise, uv * 0.23).r * 0.56
          + texture2D(uNoise, uv * 0.71 + 0.37).g * 0.29
          + texture2D(uNoise, uv * 2.13 + 0.71).b * 0.15;
  float d = smoothstep(uCloudCoverage, uCloudCoverage + 0.3, n);
  return d * smoothstep(0.015, 0.2, dir.y);
}
`;

export class Sky {
  readonly mesh: THREE.Mesh;
  /** Camera-facing sun sprite whose cloud-masked glow drives the god-rays pass. */
  readonly sun: THREE.Mesh;
  private readonly material: THREE.ShaderMaterial;

  constructor(private readonly env: Environment) {
    this.material = new THREE.ShaderMaterial({
      uniforms: this.env.uniforms,
      side: THREE.BackSide,
      depthWrite: false,
      depthTest: false,
      vertexShader: /* glsl */ `
        varying vec3 vDir;
        void main() {
          vDir = position;
          vec4 p = projectionMatrix * mat4(mat3(viewMatrix)) * vec4(position, 1.0);
          gl_Position = p.xyww;
        }
      `,
      fragmentShader: /* glsl */ `
        ${COMMON_UNIFORMS}
        varying vec3 vDir;
        ${SKY_FNS}
        ${CLOUD_FNS}
        void main() {
          vec3 dir = normalize(vDir);
          vec3 col = skyBase(dir);
          float mu = clamp(dot(dir, uSunDir), -1.0, 1.0);

          float d = cloudDensity(dir);
          float dl = cloudDensity(normalize(dir + uSunDir * 0.07));
          float transmit = exp(-dl * 2.4);
          float fwd = pow(max(mu, 0.0), 6.0);
          float silver = pow(max(mu, 0.0), 40.0);
          vec3 cloudAmbient = mix(uSkyZenith * 1.15, uSkyHorizon * 0.9, 0.45 + 0.3 * (1.0 - dir.y)) * 0.82;
          vec3 cloudLit = uSunColor * transmit * (0.17 + 0.6 * fwd + 2.2 * silver);
          vec3 cloudCol = cloudAmbient * (1.0 - 0.35 * d) + cloudLit * (1.0 - 0.5 * d);
          // Clouds sink into the horizon haze.
          cloudCol = mix(cloudCol, skyBase(dir), smoothstep(0.35, 0.0, dir.y) * 0.6);
          col = mix(col, cloudCol, d * 0.9);

          float disc = smoothstep(0.99968, 0.99988, mu);
          col += uSunColor * disc * 24.0 * (1.0 - d * 0.85);
          // Faint dither kills banding in the smooth gradient.
          col += (fract(sin(dot(gl_FragCoord.xy, vec2(12.9898, 78.233))) * 43758.5453) - 0.5) / 255.0;
          gl_FragColor = vec4(col, 1.0);
        }
      `,
    });
    this.mesh = new THREE.Mesh(new THREE.SphereGeometry(100, 48, 24), this.material);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = -10;
    this.mesh.name = 'sky';

    const sunMaterial = new THREE.ShaderMaterial({
      uniforms: this.env.uniforms,
      transparent: true,
      depthWrite: false,
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        varying vec3 vDir;
        void main() {
          vUv = uv;
          vec4 wp = modelMatrix * vec4(position, 1.0);
          vDir = normalize(wp.xyz - cameraPosition);
          gl_Position = projectionMatrix * viewMatrix * wp;
        }
      `,
      fragmentShader: /* glsl */ `
        ${COMMON_UNIFORMS}
        varying vec2 vUv;
        varying vec3 vDir;
        ${CLOUD_FNS}
        void main() {
          float r = length(vUv - 0.5) * 2.0;
          float glow = pow(max(1.0 - r, 0.0), 2.2);
          float d = cloudDensity(vDir);
          vec3 col = uSunColor * 0.2 * glow * (1.0 - d * 0.9);
          gl_FragColor = vec4(col, 1.0);
        }
      `,
    });
    this.sun = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), sunMaterial);
    this.sun.name = 'sun-sprite';
    this.sun.frustumCulled = false;
  }

  update(camera: THREE.Camera): void {
    const distance = 2500;
    this.sun.position.copy(camera.position).addScaledVector(this.env.sunDirection, distance);
    this.sun.quaternion.copy(camera.quaternion);
    this.sun.scale.setScalar(distance * 0.22);
    this.sun.updateMatrixWorld();
  }
}
